// External libraries
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Redux actions and selectors
import { fetchShifts } from '../../../../redux/actions/shiftActions';
import { fetchWeather } from '../../../../redux/actions/weatherActions';
import { selectCurrentVenue } from '../../../../redux/slices/currentUserSlice';

// Components
import EmployeeInfo from '../../../EmployeeInfo/EmployeeInfo';
import ShiftDayEntry from '../ShiftDayEntry/ShiftDayEntry';
import EmployeeWeekNavigator from '../EmployeeWeekNavigator/EmployeeWeekNavigator';

// Styled components
import { DrawerContentContainer } from '../../../AdminDrawer/AdminDrawer.styles';
import { CustomListContainer } from '../../../CustomList/CustomList.styles';

// Utils and hooks
import { useWeekDays } from '../../../../utils/Hooks/dates/useWeekDays';
import { useWeatherData } from '../../../../utils/Hooks/weather/useWeatherData';
import { getShiftsForDate, getWeeklyShiftCount } from '../../../../utils/shiftUtils';

const EmployeeShiftView = ({ employee }) => {
  const dispatch = useDispatch();

  // Local state variables
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Redux state variables
  const { startOfWeekIndex, address } = useSelector(selectCurrentVenue);
  const shifts = useSelector((state) => state.shifts.shifts);
  const employeeShifts = shifts.filter((shift) => shift.assignedName === employee?.displayName);

  // Variables for the current week from custom hook
  const { weekDays: currentWeek, startOfWeek: startOfWeekTimestamp } = useWeekDays(selectedDate);
  const weeklyShiftCount = getWeeklyShiftCount(employeeShifts, startOfWeekTimestamp);

  // Reset the selected date when employee changes
  useEffect(() => {
    setSelectedDate(new Date());
  }, [employee]);

  // Fetch data when needed
  useEffect(() => {
    dispatch(fetchShifts());
    dispatch(fetchWeather(address));
  }, [selectedDate, address, dispatch]);

  // Get weather icon for a specific date
  const { getWeatherIconForDate } = useWeatherData();

  return (
    <DrawerContentContainer alignment="center">
      <EmployeeInfo employee={employee} headerType="name" />
      <EmployeeWeekNavigator
        startOfWeekTimestamp={startOfWeekTimestamp}
        shiftCount={weeklyShiftCount}
        startOfWeekIndex={startOfWeekIndex}
        onDateChange={setSelectedDate}
      />
      <CustomListContainer>
        {currentWeek.map((date, index) => {
          const shiftsOnDate = getShiftsForDate(employeeShifts, date);
          return (
            <ShiftDayEntry
              key={date.toISOString()}
              date={date}
              weatherIcon={getWeatherIconForDate(date)}
              shifts={shiftsOnDate}
              employee={employee}
              allShifts={shifts}
              isLast={index === currentWeek.length - 1}
            />
          );
        })}
      </CustomListContainer>
    </DrawerContentContainer>
  );
};

export default EmployeeShiftView;
